'use client';

import Link from 'next/link';
import { useAuth, useClerk, useUser } from '@clerk/nextjs';
import { LogOut, Settings } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { UserAvatar, NAVBAR_ACCOUNT_AVATAR_CLASS } from '@/components/layout/account-avatars';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { formatOrgRoleLabel } from '@/lib/account/org-role-label';

type UserAccountMenuProps = {
  triggerClassName?: string;
  variant?: 'dropdown' | 'sheet' | 'enterprise-sidebar';
  sheetPart?: 'trigger' | 'panel';
  sheetExpanded?: boolean;
  onSheetToggle?: () => void;
  /** Enterprise sidebar hover-expand guard while the dropdown is open. */
  onPortaledMenuOpenChange?: (open: boolean) => void;
  theme?: 'navbar' | 'enterprise';
  compactSheetHeader?: boolean;
  /** Mobile sheet header: close sheet after navigating to profile. */
  onSheetProfileNavigate?: () => void;
};

function UserAccountPanel({
  displayName,
  email,
  imageUrl,
  orgRoleLabel,
  embedded,
  onSettings,
  onSignOut,
}: {
  displayName: string;
  email: string;
  imageUrl: string;
  orgRoleLabel: string;
  embedded: boolean;
  onSettings: () => void;
  onSignOut: () => void;
}) {
  const { t } = useTranslation();

  const header = (
    <div className="flex items-center gap-2 px-1 py-1.5 text-start text-sm">
      <UserAvatar name={displayName} email={email} imageUrl={imageUrl} />
      <div className="grid min-w-0 flex-1 text-start text-sm leading-tight">
        {displayName ? <span className="truncate font-medium">{displayName}</span> : null}
        <span className="truncate text-xs text-muted-foreground">{email}</span>
        {orgRoleLabel ? (
          <span className="truncate text-xs text-muted-foreground">{orgRoleLabel}</span>
        ) : null}
      </div>
    </div>
  );

  if (embedded) {
    return (
      <div className="flex w-full flex-col gap-1 px-1 py-2">
        {header}
      </div>
    );
  }

  return (
    <>
      <DropdownMenuGroup>
        <DropdownMenuLabel className="p-0 font-normal">{header}</DropdownMenuLabel>
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        <DropdownMenuItem asChild>
          <Link href="/settings">
            <Settings />
            {t('account.settings')}
          </Link>
        </DropdownMenuItem>
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={onSignOut}
        >
          <LogOut />
          {t('navigation.signOut')}
        </DropdownMenuItem>
      </DropdownMenuGroup>
    </>
  );
}

export function UserAccountMenu({
  triggerClassName,
  variant = 'dropdown',
  sheetPart,
  sheetExpanded = false,
  onSheetToggle,
  onPortaledMenuOpenChange,
  theme = 'navbar',
  compactSheetHeader = false,
  onSheetProfileNavigate,
}: UserAccountMenuProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const { orgRole } = useAuth();
  const { user, isLoaded } = useUser();
  const { signOut } = useClerk();

  const displayName =
    user?.fullName?.trim() ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
    user?.username ||
    '';
  const email = user?.primaryEmailAddress?.emailAddress ?? '';
  const orgRoleLabel = formatOrgRoleLabel(orgRole, t);

  if (!isLoaded) {
    if (variant === 'sheet' && sheetPart === 'trigger') {
      return (
        <button
          type="button"
          disabled
          className={cn('flex size-9 items-center justify-center rounded-full', triggerClassName)}
        >
          <UserAvatar name={null} email={null} imageUrl={null} />
        </button>
      );
    }

    if (variant === 'sheet' && sheetPart === 'panel') {
      return <div className="px-1 py-2 text-sm text-muted-foreground">{t('common.loading')}</div>;
    }

    return (
      <Button variant="ghost" size="icon-sm" className="rounded-full p-0" disabled>
        <UserAvatar
          name={null}
          email={null}
          imageUrl={null}
          className={NAVBAR_ACCOUNT_AVATAR_CLASS}
        />
      </Button>
    );
  }

  if (!user) {
    return null;
  }

  const panel = (
    <UserAccountPanel
      displayName={displayName}
      email={email}
      imageUrl={user.imageUrl}
      orgRoleLabel={orgRoleLabel}
      embedded={variant === 'sheet'}
      onSettings={() => router.push('/settings')}
      onSignOut={() => void signOut({ redirectUrl: '/' })}
    />
  );

  if (variant === 'sheet' && sheetPart === 'trigger') {
    const isEnterpriseTheme = theme === 'enterprise';
    return (
      <button
        type="button"
        onClick={() => {
          onSheetProfileNavigate?.();
          router.push('/settings');
        }}
        aria-label={t('account.settings')}
        className={cn(
          'flex shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors touch-manipulation',
          compactSheetHeader ? 'size-8' : isEnterpriseTheme ? 'size-11' : 'size-9',
          isEnterpriseTheme ? 'hover:bg-emerald-800/40' : 'hover:bg-gray-100/80 dark:hover:bg-gray-800/80',
          triggerClassName,
        )}
      >
        <UserAvatar
          name={displayName}
          email={email}
          imageUrl={user.imageUrl}
          className={
            compactSheetHeader
              ? 'size-7'
              : isEnterpriseTheme
                ? 'size-8 ring-2 ring-emerald-500/40'
                : undefined
          }
        />
      </button>
    );
  }

  if (variant === 'sheet' && sheetPart === 'panel') {
    return panel;
  }

  if (variant === 'enterprise-sidebar') {
    return (
      <DropdownMenu onOpenChange={onPortaledMenuOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn('size-8 rounded-full p-0 hover:bg-emerald-800/40', triggerClassName)}
            aria-label={email || displayName || t('account.account')}
          >
            <UserAvatar
              name={displayName}
              email={email}
              imageUrl={user.imageUrl}
              className="size-8 ring-2 ring-emerald-500/40"
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          data-enterprise-account-portal
          className="min-w-56"
          align="end"
          sideOffset={4}
        >
          {panel}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          className={cn('rounded-full p-0', triggerClassName)}
          aria-label={email || displayName || t('account.account')}
        >
          <UserAvatar
            name={displayName}
            email={email}
            imageUrl={user.imageUrl}
            className={NAVBAR_ACCOUNT_AVATAR_CLASS}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-56" align="end" sideOffset={8}>
        {panel}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
